/**
 * API Client Configuration
 * Axios instance with interceptors for authentication and error handling
 */

import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { supabase } from './supabase';
import type { ApiResponse, ApiError } from '../types/api.types';

const baseURL = process.env.VITE_API_BASE_URL || 'http://localhost:3000/api';

/**
 * Axios instance for API requests
 */
export const apiClient = axios.create({
  baseURL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 90000, // 90 seconds (LLM queries can be slow)
});

/**
 * Request interceptor to add authentication token
 */
apiClient.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();

      if (session?.access_token) {
        config.headers.Authorization = `Bearer ${session.access_token}`;
      }
    } catch (error) {
      console.error('Failed to get session:', error);
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

/**
 * Response interceptor for error handling
 */
apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<ApiResponse>) => {
    if (error.response) {
      const { status, data } = error.response;

      // Handle 401 Unauthorized - sign out and redirect to login
      if (status === 401) {
        await supabase.auth.signOut();
        window.location.href = '/login';
        return Promise.reject(error);
      }

      // Handle 429 Too Many Requests
      if (status === 429) {
        console.error('Rate limit exceeded');
        // Could show a toast notification here
      }

      // Handle 500 Internal Server Error
      if (status >= 500) {
        console.error('Server error:', data);
      }

      // Return structured error
      return Promise.reject({
        ...error,
        apiError: data?.error as ApiError | undefined,
      });
    }

    // Network error or no response
    return Promise.reject(error);
  }
);

/**
 * Type-safe GET request
 */
export async function get<T>(url: string, config?: any): Promise<ApiResponse<T>> {
  const response = await apiClient.get<ApiResponse<T>>(url, config);
  return response.data;
}

/**
 * Type-safe POST request
 */
export async function post<T>(url: string, data?: any, config?: any): Promise<ApiResponse<T>> {
  const response = await apiClient.post<ApiResponse<T>>(url, data, config);
  return response.data;
}

/**
 * Type-safe PUT request
 */
export async function put<T>(url: string, data?: any, config?: any): Promise<ApiResponse<T>> {
  const response = await apiClient.put<ApiResponse<T>>(url, data, config);
  return response.data;
}

/**
 * Type-safe DELETE request
 */
export async function del<T>(url: string, config?: any): Promise<ApiResponse<T>> {
  const response = await apiClient.delete<ApiResponse<T>>(url, config);
  return response.data;
}

export default apiClient;
